import { makeAutoObservable, reaction } from "mobx";
import {
  MatchClutchStatsResponse,
  MatchCluthIndividualStats,
  MatchUserList,
  PlayerCalculatedStats,
  PlayerDetectedShots,
  PlayerStatsDetails,
  AllPlayersProgressCpmparisonType,
  PlayerStatsComparisonType,
} from "../../apis/types";

import { CourtCoverageOptionEnum, MatchStatsEnum } from "../../utils/data";
import { RootStore } from "../RootStore";

export const AllShots = "All Shots";

interface CourtCoverage {
  rows: number;
  columns: number;
  zones: Zones[];
}

interface Zones {
  zone: string;
  percentage: number;
}

const enum ShotStyle {
  Attack = "Attack",
  Defense = "Defense",
  Neutral = "Neutral",
}

interface PlayerShotStyle {
  attack: number;
  defense: number;
  neutral: number;
  total: number;
}

export class MatchStatsStore {
  rootStore: RootStore;
  // Data shared from MatchDetailsStore
  matchClutchStatsResponse: MatchClutchStatsResponse = {};
  clutchMatchCurrentPlayer: MatchUserList = {};
  teamMates: MatchUserList[] = [];
  opponents: MatchUserList[] = [];
  colorMap: Map<number, string> = new Map();
  partnerComparisonCurrentPage: number = 0;

  // Stats-specific data
  matchClutchIndividualStats: MatchCluthIndividualStats = {};
  selectedFilteredShots: string = AllShots;
  filteredShotsMap: Map<string, PlayerDetectedShots[]> = new Map();
  courtCoverageMap: Map<string, CourtCoverage> = new Map();
  selectedCourtCoverage: string = CourtCoverageOptionEnum.ZONES;
  zonesMap: Map<string, number> = new Map();
  leftCrossPercentage: string = "0%";
  straightPercentage: string = "0%";
  rightCrossPercentage: string = "0%";
  playerStatsMap: Map<number, PlayerStatsDetails> = new Map();
  playerShotStyleMap: Map<number, PlayerShotStyle> = new Map();
  teamStatsMap: Map<number, PlayerStatsDetails> = new Map();
  height: number = 0;
  width: number = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });

    // Update individual stats when player or stats response changes
    reaction(
      () => ({
        player: this.clutchMatchCurrentPlayer,
        statsResponse: this.matchClutchStatsResponse,
        playerStats: this.playerShotStyleMap.size,
      }),
      () => {
        this.matchClutchIndividualStats =
          this.matchClutchStatsResponse.apiPlayerDetectedShots?.[0]?.individualStats?.find(
            (stats) =>
              stats.resPartnerId?.toString() ===
              this.clutchMatchCurrentPlayer.userId?.toString()
          ) || {};

        this.filteredShotsMap.clear();
        this.filteredShotsMap.set(
          AllShots,
          this.matchClutchIndividualStats.playerDetectedShotsList ?? []
        );
        this.matchClutchIndividualStats.playerDetectedShotsList?.forEach(
          (item) => {
            const key = item.shotLabel;
            if (key) {
              if (this.filteredShotsMap.has(key)) {
                this.filteredShotsMap.get(key)?.push(item);
              } else {
                this.filteredShotsMap.set(key, [item]);
              }
            }
          }
        );
        this.sortFilterShotsMap();
        this.calculateCourtCoverage();
        if (this.filteredShotsMap.has(this.selectedFilteredShots)) {
          this.setSelectedFilteredShots(this.selectedFilteredShots);
        } else {
          this.setSelectedFilteredShots(AllShots);
        }
      }
    );

    // Calculate player stats when stats response changes
    reaction(
      () => ({
        statsResponse: this.matchClutchStatsResponse,
      }),
      () => {
        this.calculatePlayerStats();
        this.calculateShotStyleStats();
      }
    );

    // Calculate team stats when player stats are ready
    reaction(
      () => ({
        playerStatMap: this.playerStatsMap.size,
        teamMateLength: this.teamMates.length,
        opponentLength: this.opponents.length,
      }),
      () => {
        if (
          this.teamMates.length > 0 &&
          this.opponents.length > 0 &&
          this.playerStatsMap.size > 0
        ) {
          this.calculateStatsOfTeam();
        }
      }
    );
  }

  // Methods to receive data from MatchDetailsStore
  setMatchClutchStatsResponse = (response: MatchClutchStatsResponse) => {
    this.matchClutchStatsResponse = response;
  };

  setClutchMatchCurrentPlayer = (player: MatchUserList) => {
    this.clutchMatchCurrentPlayer = player;
  };

  setTeammates = (teammates: MatchUserList[]) => {
    this.teamMates = teammates;
  };

  setOpponents = (opponents: MatchUserList[]) => {
    this.opponents = opponents;
  };

  setColorMap = (colorMap: Map<number, string>) => {
    this.colorMap = colorMap;
  };

  sortFilterShotsMap = () => {
    // Create a new sorted map to ensure keys are alphabetically sorted
    // const sortedKeys = [...this.filteredShotsMap.keys()].sort();
    const sortedKeys = Array.from(this.filteredShotsMap.keys()).sort();
    const newMap = new Map();

    // Add existing entries in alphabetical order
    sortedKeys.forEach((sortedKey) => {
      newMap.set(sortedKey, this.filteredShotsMap.get(sortedKey) || []);
    });

    // Replace the old map with the sorted one
    this.filteredShotsMap = newMap;
  };

  calculateShotStyleStats = () => {
    this.playerShotStyleMap.clear();
    this.matchClutchStatsResponse.apiPlayerDetectedShots?.[0].individualStats?.forEach(
      (item) => {
        const key = item.resPartnerId;
        if (key) {
          this.playerShotStyleMap.set(key, {
            attack: 0,
            defense: 0,
            neutral: 0,
            total: 0,
          });
        }
      }
    );
    this.matchClutchStatsResponse.apiPlayerDetectedShots?.[0].individualStats?.forEach(
      (item) => {
        item?.playerDetectedShotsList?.forEach((shot) => {
          if (shot.normalizedCoordinates) {
            let coordinates = JSON.parse(shot.normalizedCoordinates);
            if (coordinates.y < 0.5) {
              coordinates.x = 1 - coordinates.x;
              coordinates.y = 1 - coordinates.y;
            }
            let shotStyle = ShotStyle.Neutral;
            if (coordinates.y < 0.65) {
              shotStyle = ShotStyle.Attack;
            } else if (coordinates.y < 0.8) {
              shotStyle = ShotStyle.Neutral;
            } else {
              shotStyle = ShotStyle.Defense;
            }

            let finalShotStyle = shotStyle;
            switch (shot?.shotLabel?.toLowerCase()) {
              case "drive":
                switch (shotStyle) {
                  case ShotStyle.Attack:
                  case ShotStyle.Neutral:
                    finalShotStyle = ShotStyle.Attack;
                    break;
                  case ShotStyle.Defense:
                    finalShotStyle = ShotStyle.Neutral;
                    break;
                }
                break;
              case "serve":
                finalShotStyle = ShotStyle.Neutral;
                break;
              case "lob":
                finalShotStyle = ShotStyle.Defense;
                break;
              case "volley":
                switch (shotStyle) {
                  case ShotStyle.Attack:
                  case ShotStyle.Neutral:
                    finalShotStyle = ShotStyle.Attack;
                    break;
                  case ShotStyle.Defense:
                    finalShotStyle = ShotStyle.Defense;
                    break;
                }
                break;
              case "overhead":
                finalShotStyle = ShotStyle.Attack;
                break;
            }

            switch (finalShotStyle) {
              case ShotStyle.Attack:
                if (this.playerShotStyleMap.get(item.resPartnerId ?? 0)) {
                  this.playerShotStyleMap.get(item.resPartnerId!)!.attack += 1;
                  this.playerShotStyleMap.get(item.resPartnerId!)!.total += 1;
                }
                break;
              case ShotStyle.Defense:
                if (this.playerShotStyleMap.get(item.resPartnerId ?? 0)) {
                  this.playerShotStyleMap.get(item.resPartnerId!)!.defense += 1;
                  this.playerShotStyleMap.get(item.resPartnerId!)!.total += 1;
                }
                break;
              case ShotStyle.Neutral:
                if (this.playerShotStyleMap.get(item.resPartnerId ?? 0)) {
                  this.playerShotStyleMap.get(item.resPartnerId!)!.neutral += 1;
                  this.playerShotStyleMap.get(item.resPartnerId!)!.total += 1;
                }
                break;
            }
          }
        });
      }
    );
  };

  getCourtCoverageFrontBackFlex = (row: number) => {
    const frontBackType = this.courtCoverageMap.get(this.selectedCourtCoverage)
      ?.zones[row].zone;
    switch (frontBackType) {
      case ShotStyle.Attack:
        return 3;
      case ShotStyle.Neutral:
        return 3;
      case ShotStyle.Defense:
        return 4;
      default:
        return 1;
    }
  };

  calculatePlayerStats = () => {
    this.playerStatsMap.clear();
    this.matchClutchStatsResponse.playerStatistics?.forEach((item) => {
      const key = item.resPartnerId;
      if (key) {
        let aggregatedStats: PlayerStatsDetails = {
          consistency: 0,
          attack: 0,
          physicality: 0,
          numberOfShots: 0,
          shotQuality: 0,
          clutchScore: 0,
          courtCoverage: 0,
          distanceRunMeters: 0,
        };

        const numberOfShots =
          this.matchClutchStatsResponse.apiPlayerDetectedShots?.[0].individualStats?.find(
            (stat) => stat.resPartnerId === key
          )?.playerDetectedShotsList?.length;

        // Sum up all values from player stats details
        item.playerStatsDetails.forEach((stat) => {
          aggregatedStats.consistency += stat.consistency || 0;
          aggregatedStats.attack += stat.attack || 0;
          aggregatedStats.physicality += stat.physicality || 0;
          aggregatedStats.numberOfShots += stat.numberOfShots || 0;
          aggregatedStats.shotQuality += stat.shotQuality || 0;
          aggregatedStats.clutchScore += stat.clutchScore || 0;
          aggregatedStats.courtCoverage += stat.courtCoverage || 0;
          aggregatedStats.distanceRunMeters += stat.distanceRunMeters || 0;
        });

        // Store the aggregated stats in the map
        this.playerStatsMap.set(key, {
          ...aggregatedStats,
          numberOfShots: numberOfShots ?? aggregatedStats.numberOfShots,
        });
      }
    });
  };

  calculateStatsOfTeam = () => {
    // Get player IDs
    const myId = parseInt(this.teamMates[0]?.userId ?? "0");
    const teammateId = parseInt(this.teamMates[1]?.userId ?? "0");
    const opponent1Id = parseInt(this.opponents[0]?.userId ?? "0");
    const opponent2Id = parseInt(this.opponents[1]?.userId ?? "0"); // Fixed: was using opponents[0] twice
    // Get stats from playerStatsMap
    const myStats = this.playerStatsMap.get(myId) || this.createEmptyStats();
    const teammateStats =
      this.playerStatsMap.get(teammateId) || this.createEmptyStats();
    const opponent1Stats =
      this.playerStatsMap.get(opponent1Id) || this.createEmptyStats();
    const opponent2Stats =
      this.playerStatsMap.get(opponent2Id) || this.createEmptyStats();

    // Calculate team stats by combining individual stats
    const myTeamStats = this.combinePlayerStats(myStats, teammateStats);
    const opponentsStats = this.combinePlayerStats(
      opponent1Stats,
      opponent2Stats
    );

    // Store in teamStatsMap
    const myTeamId = this.teamMates[1]?.teamId ?? 0;
    const opponentsTeamId = this.opponents[0]?.teamId ?? 0;

    this.teamStatsMap.set(myTeamId, myTeamStats);
    this.teamStatsMap.set(opponentsTeamId, opponentsStats);
  };

  // Helper methods
  createEmptyStats = (): PlayerStatsDetails => {
    return {
      consistency: 0,
      attack: 0,
      physicality: 0,
      numberOfShots: 0,
      shotQuality: 0,
      clutchScore: 0,
      courtCoverage: 0,
      distanceRunMeters: 0,
    };
  };

  // Helper method to combine two player stats
  combinePlayerStats = (
    stats1: PlayerStatsDetails,
    stats2: PlayerStatsDetails
  ): PlayerStatsDetails => {
    return {
      consistency: stats1.consistency + stats2.consistency,
      attack: stats1.attack + stats2.attack,
      physicality: stats1.physicality + stats2.physicality,
      numberOfShots: stats1.numberOfShots + stats2.numberOfShots,
      shotQuality: stats1.shotQuality + stats2.shotQuality,
      clutchScore: stats1.clutchScore + stats2.clutchScore,
      courtCoverage: stats1.courtCoverage + stats2.courtCoverage,
      distanceRunMeters: stats1.distanceRunMeters + stats2.distanceRunMeters,
    };
  };

  getmyTeamDistanceStats = (): PlayerCalculatedStats => {
    const distance =
      this.teamStatsMap.get(this.teamMates[1]?.teamId ?? 0)
        ?.distanceRunMeters ?? 0;
    const totalDistance =
      (this.teamStatsMap.get(this.teamMates[1]?.teamId ?? 0)
        ?.distanceRunMeters ?? 0) +
      (this.teamStatsMap.get(this.opponents[0]?.teamId ?? 0)
        ?.distanceRunMeters ?? 0);

    return {
      value: Math.round(distance),
      percentage: Math.round((distance / totalDistance) * 100),
    };
  };

  getOpponentsDistanceStats = () => {
    const distance =
      this.teamStatsMap.get(this.opponents[0]?.teamId ?? 0)
        ?.distanceRunMeters ?? 0;

    const totalDistance =
      (this.teamStatsMap.get(this.teamMates[1]?.teamId ?? 0)
        ?.distanceRunMeters ?? 0) +
      (this.teamStatsMap.get(this.opponents[0]?.teamId ?? 0)
        ?.distanceRunMeters ?? 0);

    return {
      value: Math.round(distance),
      percentage: Math.round((distance / totalDistance) * 100),
    };
  };

  getmyTeamNumberOfShotsStats = (): PlayerCalculatedStats => {
    const shots =
      this.teamStatsMap.get(this.teamMates[1]?.teamId ?? 0)?.numberOfShots ?? 0;

    const totalShots =
      (this.teamStatsMap.get(this.teamMates[1]?.teamId ?? 0)?.numberOfShots ??
        0) +
      (this.teamStatsMap.get(this.opponents[0]?.teamId ?? 0)?.numberOfShots ??
        0);

    return {
      value: shots,
      percentage: Math.round((shots / totalShots) * 100),
    };
  };

  getOpponentsNumberOfShotsStats = () => {
    const shots =
      this.teamStatsMap.get(this.opponents[0]?.teamId ?? 0)?.numberOfShots ?? 0;

    const totalShots =
      (this.teamStatsMap.get(this.teamMates[1]?.teamId ?? 0)?.numberOfShots ??
        0) +
      (this.teamStatsMap.get(this.opponents[0]?.teamId ?? 0)?.numberOfShots ??
        0);

    return {
      value: shots,
      percentage: Math.round((shots / totalShots) * 100),
    };
  };

  getMyDistanceStats = (): PlayerCalculatedStats => {
    const distance =
      this.playerStatsMap.get(parseInt(this.teamMates[0]?.userId ?? "0"))
        ?.distanceRunMeters ?? 0;
    const totalDistance =
      this.teamStatsMap.get(this.teamMates[1]?.teamId ?? 0)
        ?.distanceRunMeters ?? 0;

    return {
      value: Math.round(distance),
      percentage: Math.round((distance / totalDistance) * 100),
      id: parseInt(this.teamMates[0]?.userId ?? "0"),
    };
  };

  getmyTeamPartnerDistanceStats = (): PlayerCalculatedStats => {
    const distance =
      this.playerStatsMap.get(parseInt(this.teamMates[1]?.userId ?? "0"))
        ?.distanceRunMeters ?? 0;
    const totalDistance =
      this.teamStatsMap.get(this.teamMates[1]?.teamId ?? 0)
        ?.distanceRunMeters ?? 0;

    return {
      value: Math.round(distance),
      percentage: Math.round((distance / totalDistance) * 100),
      id: parseInt(this.teamMates[1]?.userId ?? "0"),
    };
  };

  getFirstOpponentDistanceStats = (): PlayerCalculatedStats => {
    const distance =
      this.playerStatsMap.get(parseInt(this.opponents[0]?.userId ?? "0"))
        ?.distanceRunMeters ?? 0;
    const totalDistance =
      this.teamStatsMap.get(this.opponents[0]?.teamId ?? 0)
        ?.distanceRunMeters ?? 0;

    return {
      value: Math.round(distance),
      percentage: Math.round((distance / totalDistance) * 100),
      id: parseInt(this.opponents[0]?.userId ?? "0"),
    };
  };

  getSecondOpponentDistanceStats = (): PlayerCalculatedStats => {
    const distance =
      this.playerStatsMap.get(parseInt(this.opponents[1]?.userId ?? "0"))
        ?.distanceRunMeters ?? 0;
    const totalDistance =
      this.teamStatsMap.get(this.opponents[0]?.teamId ?? 0)
        ?.distanceRunMeters ?? 0;

    return {
      value: Math.round(distance),
      percentage: Math.round((distance / totalDistance) * 100),
      id: parseInt(this.opponents[1]?.userId ?? "0"),
    };
  };

  getMyNumberOfShotsStats = (): PlayerCalculatedStats => {
    const shots =
      this.playerStatsMap.get(parseInt(this.teamMates[0]?.userId ?? "0"))
        ?.numberOfShots ?? 0;
    const totalShots =
      this.teamStatsMap.get(this.teamMates[1]?.teamId ?? 0)?.numberOfShots ?? 0;

    return {
      value: Math.round(shots),
      percentage: Math.round((shots / totalShots) * 100),
      id: parseInt(this.teamMates[0]?.userId ?? "0"),
    };
  };

  getmyTeamPartnerNumberOfShotsStats = (): PlayerCalculatedStats => {
    const shots =
      this.playerStatsMap.get(parseInt(this.teamMates[1]?.userId ?? "0"))
        ?.numberOfShots ?? 0;
    const totalShots =
      this.teamStatsMap.get(this.teamMates[1]?.teamId ?? 0)?.numberOfShots ?? 0;

    return {
      value: Math.round(shots),
      percentage: Math.round((shots / totalShots) * 100),
      id: parseInt(this.teamMates[1]?.userId ?? "0"),
    };
  };

  getFirstOpponentNumberOfShotsStats = (): PlayerCalculatedStats => {
    const shots =
      this.playerStatsMap.get(parseInt(this.opponents[0]?.userId ?? "0"))
        ?.numberOfShots ?? 0;
    const totalShots =
      this.teamStatsMap.get(this.opponents[0]?.teamId ?? 0)?.numberOfShots ?? 0;

    return {
      value: Math.round(shots),
      percentage: Math.round((shots / totalShots) * 100),
      id: parseInt(this.opponents[0]?.userId ?? "0"),
    };
  };

  getSecondOpponentNumberOfShotsStats = (): PlayerCalculatedStats => {
    const shots =
      this.playerStatsMap.get(parseInt(this.opponents[1]?.userId ?? "0"))
        ?.numberOfShots ?? 0;
    const totalShots =
      this.teamStatsMap.get(this.opponents[1]?.teamId ?? 0)?.numberOfShots ?? 0;

    return {
      value: Math.round(shots),
      percentage: Math.round((shots / totalShots) * 100),
      id: parseInt(this.opponents[1]?.userId ?? "0"),
    };
  };

  calculateCourtCoverage = () => {
    this.zonesMap.clear();
    this.matchClutchIndividualStats.playerDetectedShotsList?.forEach((item) => {
      const key = item.zone?.toString() ?? "";
      if (key) {
        if (this.zonesMap.has(key)) {
          this.zonesMap.set(key, (this.zonesMap.get(key) ?? 0) + 1);
        } else {
          this.zonesMap.set(key, 1);
        }
      }
    });
    this.courtCoverageMap.clear();
    const totalZones =
      this.matchClutchIndividualStats.playerDetectedShotsList?.length ?? 1;
    const zone0 = ((this.zonesMap.get("0") ?? 0) / totalZones) * 100;
    const zone1 = ((this.zonesMap.get("1") ?? 0) / totalZones) * 100;
    const zone2 = ((this.zonesMap.get("2") ?? 0) / totalZones) * 100;
    const zone3 = ((this.zonesMap.get("3") ?? 0) / totalZones) * 100;
    const zone4 = ((this.zonesMap.get("4") ?? 0) / totalZones) * 100;
    const zone5 = ((this.zonesMap.get("5") ?? 0) / totalZones) * 100;

    // Zones
    const zones: Zones[] = [
      { zone: "Zone 0", percentage: zone0 },
      { zone: "Zone 1", percentage: zone1 },
      { zone: "Zone 2", percentage: zone2 },
      { zone: "Zone 3", percentage: zone3 },
      { zone: "Zone 4", percentage: zone4 },
      { zone: "Zone 5", percentage: zone5 },
    ];
    this.courtCoverageMap.set(CourtCoverageOptionEnum.ZONES, {
      rows: 2,
      columns: 3,
      zones,
    });

    // Sides
    const sides: Zones[] = [
      { zone: "Left", percentage: zone0 + zone3 },
      { zone: "Center", percentage: zone1 + zone4 },
      { zone: "Right", percentage: zone2 + zone5 },
    ];
    this.courtCoverageMap.set(CourtCoverageOptionEnum.SIDES, {
      rows: 1,
      columns: 3,
      zones: sides,
    });

    // Front-Back
    const player = this.playerShotStyleMap.get(
      parseInt(this.clutchMatchCurrentPlayer.userId ?? "0")
    );
    const frontBack: Zones[] = [
      {
        zone: ShotStyle.Attack,
        percentage: Math.round(
          ((player?.attack ?? 0) / (player?.total ?? 1)) * 100
        ),
      },
      {
        zone: ShotStyle.Neutral,
        percentage: Math.round(
          ((player?.neutral ?? 0) / (player?.total ?? 1)) * 100
        ),
      },
      {
        zone: ShotStyle.Defense,
        percentage: Math.round(
          ((player?.defense ?? 0) / (player?.total ?? 1)) * 100
        ),
      },
    ];

    this.courtCoverageMap.set(CourtCoverageOptionEnum.FRONT_BACK, {
      rows: 3,
      columns: 1,
      zones: frontBack,
    });
    // this.setSelectedCourtCoverage('Zones');
  };

  setSelectedCourtCoverage = (selectedCourtCoverage: string) => {
    this.selectedCourtCoverage = selectedCourtCoverage;
  };

  setSelectedFilteredShots = (selectedFilteredShots: string) => {
    this.selectedFilteredShots = selectedFilteredShots;
    this.calculateShotTrajectories();
  };

  setPartnerComparisonCurrentPage = (partnerComparisonCurrentPage: number) => {
    this.partnerComparisonCurrentPage = partnerComparisonCurrentPage;
  };

  calculateShotTrajectories = () => {
    let nLeft = 0;

    let nStraight = 0;

    let nRight = 0;

    this.filteredShotsMap.get(this.selectedFilteredShots)?.forEach((s) => {
      if (s.shotDirection) {
        const [pos, dir] = s.shotDirection.split("/");

        const zone = s.zone;

        if (pos === "straight") {
          if (zone === 0 || zone === 3) {
            nLeft += 1;
          } else if (zone === 2 || zone === 5) {
            nRight += 1;
          } else {
            nStraight += 1;
          }
        } else if (dir === "center") {
          nStraight += 1;
        } else if (dir === "left") {
          nLeft += 1;
        } else if (dir === "right") {
          nRight += 1;
        }
      }
    });

    const sum = nLeft + nStraight + nRight;

    if (sum === 0) {
      return {
        nLeft: 0,

        nStraight: 0,

        nRight: 0,
      };
    }

    nLeft = Math.round((nLeft / sum) * 100);

    nStraight = Math.round((nStraight / sum) * 100);

    nRight = Math.round((nRight / sum) * 100);

    this.leftCrossPercentage = `${nLeft}%`;
    this.straightPercentage = `${nStraight}%`;
    this.rightCrossPercentage = `${nRight}%`;
  };

  setHeight = (height: number) => {
    this.height = height;
  };

  setWidth = (width: number) => {
    this.width = width;
  };

  getAllPlayersProgressStats = (): AllPlayersProgressCpmparisonType[] => {
    const stats: AllPlayersProgressCpmparisonType[] = [];
    stats.push({
      title: MatchStatsEnum.DISTANCE_RUN_IN_METER,
      player1: this.getMyDistanceStats(),
      player2: this.getmyTeamPartnerDistanceStats(),
      player3: this.getFirstOpponentDistanceStats(),
      player4: this.getSecondOpponentDistanceStats(),
    });

    stats.push({
      title: MatchStatsEnum.NO_OF_SHOTS,
      player1: this.getMyNumberOfShotsStats(),
      player2: this.getmyTeamPartnerNumberOfShotsStats(),
      player3: this.getFirstOpponentNumberOfShotsStats(),
      player4: this.getSecondOpponentNumberOfShotsStats(),
    });

    const totalShots = Array.from(this.teamStatsMap.values()).reduce(
      (acc, curr) => acc + curr.numberOfShots,
      0
    );

    stats.push({
      title: MatchStatsEnum.BALL_POSSESSION,
      showPercentage: true,
      player1: {
        ...this.getMyNumberOfShotsStats(),
        percentage: Math.round(
          (this.getMyNumberOfShotsStats().value / totalShots) * 100
        ),
      },
      player2: {
        ...this.getmyTeamPartnerNumberOfShotsStats(),
        percentage: Math.round(
          (this.getmyTeamPartnerNumberOfShotsStats().value / totalShots) * 100
        ),
      },
      player3: {
        ...this.getFirstOpponentNumberOfShotsStats(),
        percentage: Math.round(
          (this.getFirstOpponentNumberOfShotsStats().value / totalShots) * 100
        ),
      },
      player4: {
        ...this.getSecondOpponentNumberOfShotsStats(),
        percentage: Math.round(
          (this.getSecondOpponentNumberOfShotsStats().value / totalShots) * 100
        ),
      },
    });

    const player1 = this.playerShotStyleMap.get(
      parseInt(this.teamMates[0]?.userId ?? "0")
    );
    const player2 = this.playerShotStyleMap.get(
      parseInt(this.teamMates[1]?.userId ?? "0")
    );
    const player3 = this.playerShotStyleMap.get(
      parseInt(this.opponents[0]?.userId ?? "0")
    );
    const player4 = this.playerShotStyleMap.get(
      parseInt(this.opponents[1]?.userId ?? "0")
    );

    stats.push({
      title: MatchStatsEnum.ATTACK,
      showPercentage: true,
      player1: {
        id: parseInt(this.teamMates[0]?.userId ?? "0"),
        value: player1?.attack ?? 0,
        percentage: Math.round(
          ((player1?.attack ?? 0) / (player1?.total ?? 1)) * 100
        ),
      },
      player2: {
        id: parseInt(this.teamMates[1]?.userId ?? "0"),
        value: player2?.attack ?? 0,
        percentage: Math.round(
          ((player2?.attack ?? 0) / (player2?.total ?? 1)) * 100
        ),
      },
      player3: {
        id: parseInt(this.opponents[0]?.userId ?? "0"),
        value: player3?.attack ?? 0,
        percentage: Math.round(
          ((player3?.attack ?? 0) / (player3?.total ?? 1)) * 100
        ),
      },
      player4: {
        id: parseInt(this.opponents[1]?.userId ?? "0"),
        value: player4?.attack ?? 0,
        percentage: Math.round(
          ((player4?.attack ?? 0) / (player4?.total ?? 1)) * 100
        ),
      },
    });

    stats.push({
      title: MatchStatsEnum.NEUTRAL,
      showPercentage: true,
      player1: {
        id: parseInt(this.teamMates[0]?.userId ?? "0"),
        value: player1?.neutral ?? 0,
        percentage: Math.round(
          ((player1?.neutral ?? 0) / (player1?.total ?? 1)) * 100
        ),
      },
      player2: {
        id: parseInt(this.teamMates[1]?.userId ?? "0"),
        value: player2?.neutral ?? 0,
        percentage: Math.round(
          ((player2?.neutral ?? 0) / (player2?.total ?? 1)) * 100
        ),
      },
      player3: {
        id: parseInt(this.opponents[0]?.userId ?? "0"),
        value: player3?.neutral ?? 0,
        percentage: Math.round(
          ((player3?.neutral ?? 0) / (player3?.total ?? 1)) * 100
        ),
      },
      player4: {
        id: parseInt(this.opponents[1]?.userId ?? "0"),
        value: player4?.neutral ?? 0,
        percentage: Math.round(
          ((player4?.neutral ?? 0) / (player4?.total ?? 1)) * 100
        ),
      },
    });

    stats.push({
      title: MatchStatsEnum.DEFENSE,
      showPercentage: true,
      player1: {
        id: parseInt(this.teamMates[0]?.userId ?? "0"),
        value: player1?.defense ?? 0,
        percentage: Math.round(
          ((player1?.defense ?? 0) / (player1?.total ?? 1)) * 100
        ),
      },
      player2: {
        id: parseInt(this.teamMates[1]?.userId ?? "0"),
        value: player2?.defense ?? 0,
        percentage: Math.round(
          ((player2?.defense ?? 0) / (player2?.total ?? 1)) * 100
        ),
      },
      player3: {
        id: parseInt(this.opponents[0]?.userId ?? "0"),
        value: player3?.defense ?? 0,
        percentage: Math.round(
          ((player3?.defense ?? 0) / (player3?.total ?? 1)) * 100
        ),
      },
      player4: {
        id: parseInt(this.opponents[1]?.userId ?? "0"),
        value: player4?.defense ?? 0,
        percentage: Math.round(
          ((player4?.defense ?? 0) / (player4?.total ?? 1)) * 100
        ),
      },
    });
    return stats;
  };

  getPartnerComparisonStats = (): PlayerStatsComparisonType[] => {
    const stats: PlayerStatsComparisonType[] = [];
    stats.push({
      title: MatchStatsEnum.DISTANCE_RUN_IN_METER,
      player1:
        this.partnerComparisonCurrentPage === 0
          ? this.getMyDistanceStats()
          : this.getFirstOpponentDistanceStats(),
      player2:
        this.partnerComparisonCurrentPage === 0
          ? this.getmyTeamPartnerDistanceStats()
          : this.getSecondOpponentDistanceStats(),
    });

    // Add shot count stats
    stats.push({
      title: MatchStatsEnum.NO_OF_SHOTS,
      player1:
        this.partnerComparisonCurrentPage === 0
          ? this.getMyNumberOfShotsStats()
          : this.getFirstOpponentNumberOfShotsStats(),
      player2:
        this.partnerComparisonCurrentPage === 0
          ? this.getmyTeamPartnerNumberOfShotsStats()
          : this.getSecondOpponentNumberOfShotsStats(),
    });

    // Add ball possession stats
    stats.push({
      title: MatchStatsEnum.BALL_POSSESSION,
      showPercentage: true,
      player1:
        this.partnerComparisonCurrentPage === 0
          ? this.getMyNumberOfShotsStats()
          : this.getFirstOpponentNumberOfShotsStats(),
      player2:
        this.partnerComparisonCurrentPage === 0
          ? this.getmyTeamPartnerNumberOfShotsStats()
          : this.getSecondOpponentNumberOfShotsStats(),
    });

    const player1 =
      this.partnerComparisonCurrentPage === 0
        ? this.teamMates[0]
        : this.opponents[0];
    const player2 =
      this.partnerComparisonCurrentPage === 0
        ? this.teamMates[1]
        : this.opponents[1];

    const player1Attack =
      this.playerShotStyleMap.get(parseInt(player1.userId ?? "0"))?.attack ?? 0;
    const player2Attack =
      this.playerShotStyleMap.get(parseInt(player2.userId ?? "0"))?.attack ?? 0;

    const player1Neutral =
      this.playerShotStyleMap.get(parseInt(player1.userId ?? "0"))?.neutral ??
      0;
    const player2Neutral =
      this.playerShotStyleMap.get(parseInt(player2.userId ?? "0"))?.neutral ??
      0;

    const player1Defense =
      this.playerShotStyleMap.get(parseInt(player1.userId ?? "0"))?.defense ??
      0;
    const player2Defense =
      this.playerShotStyleMap.get(parseInt(player2.userId ?? "0"))?.defense ??
      0;

    const player1Total =
      this.playerShotStyleMap.get(parseInt(player1.userId ?? "0"))?.total ?? 0;
    const player2Total =
      this.playerShotStyleMap.get(parseInt(player2.userId ?? "0"))?.total ?? 0;

    // const totalAttack = player1Attack + player2Attack;
    // const totalNeutral = player1Neutral + player2Neutral;
    // const totalDefense = player1Defense + player2Defense;

    stats.push({
      title: MatchStatsEnum.ATTACK,
      showPercentage: true,
      player1: {
        value: player1Attack,
        percentage: Math.round((player1Attack / player1Total) * 100),
      },
      player2: {
        value: player2Attack,
        percentage: Math.round((player2Attack / player2Total) * 100),
      },
    });

    stats.push({
      title: MatchStatsEnum.NEUTRAL,
      showPercentage: true,
      player1: {
        value: player1Neutral,
        percentage: Math.round((player1Neutral / player1Total) * 100),
      },
      player2: {
        value: player2Neutral,
        percentage: Math.round((player2Neutral / player2Total) * 100),
      },
    });

    stats.push({
      title: MatchStatsEnum.DEFENSE,
      showPercentage: true,
      player1: {
        value: player1Defense,
        percentage: Math.round((player1Defense / player1Total) * 100),
      },
      player2: {
        value: player2Defense,
        percentage: Math.round((player2Defense / player2Total) * 100),
      },
    });
    return stats;
  };

  getTeamComparisonStats = (): PlayerStatsComparisonType[] => {
    const stats: PlayerStatsComparisonType[] = [];

    // Add distance stats
    stats.push({
      title: MatchStatsEnum.DISTANCE_RUN_IN_METER,
      player1: this.getmyTeamDistanceStats(),
      player2: this.getOpponentsDistanceStats(),
    });

    // Add shot count stats
    stats.push({
      title: MatchStatsEnum.NO_OF_SHOTS,
      player1: this.getmyTeamNumberOfShotsStats(),
      player2: this.getOpponentsNumberOfShotsStats(),
    });

    // Ball possession stats
    stats.push({
      title: MatchStatsEnum.BALL_POSSESSION,
      showPercentage: true,
      player1: this.getmyTeamNumberOfShotsStats(),
      player2: this.getOpponentsNumberOfShotsStats(),
    });

    // Helper function to safely get player shot style
    const getPlayerShotStyle = (
      userId: string | undefined
    ): PlayerShotStyle => {
      if (!userId) return { attack: 0, defense: 0, neutral: 0, total: 0 };
      return (
        this.playerShotStyleMap.get(parseInt(userId)) || {
          attack: 0,
          defense: 0,
          neutral: 0,
          total: 0,
        }
      );
    };

    // Helper function to calculate team shot styles
    const calculateTeamShotStyle = (
      players: MatchUserList[]
    ): PlayerShotStyle => {
      const result: PlayerShotStyle = {
        attack: 0,
        defense: 0,
        neutral: 0,
        total: 0,
      };

      players.forEach((player) => {
        if (player?.userId) {
          const playerStyle = getPlayerShotStyle(player.userId);
          result.attack += playerStyle.attack;
          result.defense += playerStyle.defense;
          result.neutral += playerStyle.neutral;
          result.total += playerStyle.total;
        }
      });

      return result;
    };

    // Calculate shot styles for both teams
    const myTeamShotsStyle = calculateTeamShotStyle(this.teamMates);
    const opponentShotsStyle = calculateTeamShotStyle(this.opponents);

    // Helper function to create percentage-based stat
    const createPercentageStat = (
      title: string,
      myTeamValue: number,
      opponentValue: number,
      myTeamTotal: number,
      opponentTotal: number
    ) => {
      return {
        title,
        showPercentage: true,
        player1: {
          value: myTeamValue,
          percentage:
            myTeamTotal > 0 ? Math.round((myTeamValue / myTeamTotal) * 100) : 0,
        },
        player2: {
          value: opponentValue,
          percentage:
            opponentTotal > 0
              ? Math.round((opponentValue / opponentTotal) * 100)
              : 0,
        },
      };
    };

    // Add shot style stats
    stats.push(
      createPercentageStat(
        MatchStatsEnum.ATTACK,
        myTeamShotsStyle.attack,
        opponentShotsStyle.attack,
        myTeamShotsStyle.total,
        opponentShotsStyle.total
      )
    );
    stats.push(
      createPercentageStat(
        MatchStatsEnum.NEUTRAL,
        myTeamShotsStyle.neutral,
        opponentShotsStyle.neutral,
        myTeamShotsStyle.total,
        opponentShotsStyle.total
      )
    );
    stats.push(
      createPercentageStat(
        MatchStatsEnum.DEFENSE,
        myTeamShotsStyle.defense,
        opponentShotsStyle.defense,
        myTeamShotsStyle.total,
        opponentShotsStyle.total
      )
    );

    return stats;
  };

  reset() {
    this.matchClutchStatsResponse = {};
    this.clutchMatchCurrentPlayer = {};
    this.matchClutchIndividualStats = {};
    this.selectedFilteredShots = AllShots;
    this.filteredShotsMap.clear();
    this.courtCoverageMap.clear();
    this.selectedCourtCoverage = CourtCoverageOptionEnum.ZONES;
    this.zonesMap.clear();
    this.leftCrossPercentage = "0%";
    this.straightPercentage = "0%";
    this.rightCrossPercentage = "0%";
    this.playerStatsMap.clear();
    this.playerShotStyleMap.clear();
    this.teamStatsMap.clear();
    this.teamMates = [];
    this.opponents = [];
    this.colorMap.clear();
    this.height = 0;
    this.width = 0;
    this.partnerComparisonCurrentPage = 0;
  }
}
